/**
 * Studio V2's composition hook — the ONE place the workspace's three sources
 * (the derivation engine, the raw statement read, the V2 URL) are joined into
 * what the shell renders. Nothing here derives a NEW fact about the
 * declaration; it reshapes existing derivations (`useDeclarationFlowState`)
 * and the pure R1–R4 cascade (`mapping/deriveFrameworkVerdict`) into a
 * per-framework view.
 *
 * `useDeclarationStatements(declId)` is called again here even though
 * `useDeclarationFlowState` already calls it internally — both resolve to the
 * SAME react-query key (`["mc","declarations",declId,"statements"]`), so this
 * is one network fetch, shared from cache, not two.
 */
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import type {
  CoveredRef,
  IssuerScopeProposal,
  ReaderHint,
  ScopeChain,
  ScopeFlagFact,
  UncoveredClaim,
} from "@/features/material-compliance/api/types"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { QueryClientContext } from "@tanstack/react-query"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { getApiErrorCode, getApiErrorStatus } from "../../../../api/apiError"
import {
  inquiryArticleCountFor,
  inquiryIsDeadAddressFor,
  inquiryStateFor,
  useDeclarationInquiries,
  type DeclarationInquiryState,
} from "../../../../api/declarationInquiriesApi"
import { useDeclarationQuestions } from "../../../../api/declarationQuestionsApi"
import {
  frameworkSliceKey,
  prefetchFrameworkSlice,
  refreshOpenItems,
  useDeclarationExtraction,
  useDeclarationFramework,
  useDeclarationOpenItems,
  useDeclarationScope,
  useDeclarationStatements,
  type DeclarationFrameworkSlice,
} from "../../../../api/declarationsApi"
import type {
  CandidateArticle,
  CellQuote,
  ConditionFillFinding,
  DeclarationOpenItemsWire,
  DeclarationReadModel,
  EvidenceAnchor,
  FrameworkSummaryWire,
  MemorySupplierHint,
  StatementCondition,
  StatementGroup,
  StatementGroupEntry,
  SupplierNameSuggestion,
} from "../../../../api/types"
import { useReviewPaged } from "../../../../hooks/useReviewPaged"
import {
  scopeKey,
  type StateContext,
  type WorklistIssue,
} from "../studio/cellState"
import { verifiedKey } from "../studio/conditions"
import {
  blockingFindingArticles,
  poolFallbackArticles,
} from "../studio/flowState"
import {
  isOldOpenItemsBackend,
  mergeWorklist,
  openItemsStatus,
  openItemsToWorklist,
  openItemsUnsettled,
  type OpenItemsStatus,
} from "../studio/serverWorklist"
import type { Verdict } from "../studio/studioTypes"
import { useDeclarationFlowState } from "../studio/useDeclarationFlowState"
import { orderCellProofs, parseA1Range } from "../studio/workbookProofs"
import {
  deriveFrameworkVerdict,
  derivePrimaryAction,
  type FrameworkDerivation,
  type InquiryState,
  type PrimaryAction,
  type UnitFacts,
} from "./mapping/deriveFrameworkVerdict"
import {
  editionInfoByFramework,
  type FrameworkEditionInfo,
} from "./mapping/editionInfo"
import { frameworkDisplayName } from "./mapping/frameworkDisplayName"
import {
  frameworkGeprueft,
  openWorkByFramework,
} from "./mapping/frameworkGeprueft"
import {
  openPointDetail,
  type OpenPointDetail,
} from "./mapping/openPointDetail"
import {
  cardRefsFromSummary,
  collisionsFromSummary,
  deriveFromSummary,
  documentOrderFromSummary,
  unitsFromSummary,
  type CardRef,
} from "./mapping/pagedSummary"
import { comparePosition } from "./mapping/position"
import { useStudioV2Url, type StudioV2Url } from "./useStudioV2Url"

export interface StudioV2State {
  declId: string
  ctx: StateContext | null
  /** The orion read model, verbatim. NOT what the document pane's viewer
   *  dispatch reads any more (`StudioV2Layout`'s `isWorkbook` derives that
   *  from the declaration's own file name instead — this field's own
   *  `document_kind` is absent until a run has completed, which used to open
   *  the wrong viewer on a workbook for as long as extraction was pending or
   *  re-running). Still the source for `surfaces`/cell-decoration data once a
   *  run has completed. */
  readModel: DeclarationReadModel | null
  worklist: WorklistIssue[]
  /** Unique framework slugs in document order (`documentOrder`): the
   *  navigator's row order, and the order Weiter and the open-points map walk. */
  frameworks: string[]
  /** Server-derived regulation name per framework slug (StatementGroup's own
   *  `regulationName` — never re-derived client-side), for the navigator row
   *  and the review mask header. Falls back to the bare slug when a framework
   *  carries no name (a declaration with no statement read yet). */
  regulationNameByFramework: Map<string, string | null>
  /** `url.state.framework` when it names a framework of this declaration,
   *  else the first one. Null only when the declaration has none at all. */
  activeFramework: string | null
  /** Per framework, the units `deriveFrameworkVerdict` reads — joined from
   *  `ctx.statements` (verdict, groupIndex) and the statement read (scopeAll,
   *  articleNumbers). EXCLUDES `card.excluded` (rejected) statements — a
   *  rejected statement counts for nothing in the cascade any more (BUG B
   *  fix); a framework whose statements are ALL excluded ends up with an
   *  empty list here, which is exactly what makes `deriveFrameworkVerdict`
   *  return null for it (its own documented `units.length === 0` case). */
  unitsByFramework: Map<string, UnitFacts[]>
  /** Per framework, the SAME join with the statement's own basis text kept on
   *  — what the read-only unit card quotes ("Aussage laut Dokument"). A
   *  superset of `unitsByFramework`'s facts, not a second derivation of them. */
  statementCardsByFramework: Map<string, StatementCard[]>
  /** Referenzliste per Verordnung — catalogue ∪ card values, the selected
   *  edition's name, and document-cited unknown editions per group index
   *  (`mapping/editionInfo.ts`). Empty for frameworks without an edition
   *  axis; the unit card's REFERENZLISTE row reads this. */
  editionInfo: Map<string, FrameworkEditionInfo>
  /** Per framework, the R1–R4 display derivation (see `deriveFrameworkVerdict`
   *  for why this never gates anything). */
  derivations: Map<string, FrameworkDerivation | null>
  /** The gate predicate (§ frameworkGeprueft.ts) — override-adjusted worklist,
   *  filtered per framework. */
  geprueft: (framework: string) => boolean
  /** Open worklist issues grouped per framework, worklist order preserved. */
  openByFramework: Map<string, WorklistIssue[]>
  /** Paged mode (C1-d): where the server's open items stand — "fresh",
   *  "refreshing" (an answer shown, a newer one on its way after a gesture),
   *  "pending" (no answer yet) or "unknown" (the read failed). Null when the
   *  screen derives everything itself (flag off, or a backend older than C3-s).
   *  Finish waits unless "fresh". */
  openItemsStatus?: OpenItemsStatus | null
  /** Paged mode (C1-d): a regulation whose ✓ must not be trusted — "pending" (no
   *  server answer yet) or "unknown" (the read failed), for every regulation but
   *  the open one (that one is the browser's own derivation). Null otherwise,
   *  including while a newer answer is on its way ("refreshing"). */
  regulationStatusOf?: (framework: string) => "pending" | "unknown" | null
  /** The declaration-level primary action, from every framework's derivation. */
  primaryAction: PrimaryAction
  /** Per-framework Rückfrage state (`GET .../inquiries`, D2) — feeds
   *  `deriveFrameworkVerdict`'s R1, the navigator row's mail glyph, and the
   *  footer's Übernehmen gate. Absent from the read = "none" (never "draft"
   *  — that state is the AskBox composer's own local one). */
  inquiryStateOf: (
    framework: string,
  ) => Exclude<DeclarationInquiryState, "draft">
  /** Articles the framework's open Rückfrage names (0 when none): a non-zero
   *  count means approve holds those back instead of refusing. */
  inquiryArticleCountOf: (framework: string) => number
  /** Whether the framework's open Rückfrage went to a dead address (bounced or
   *  spam-flagged). Separate from the count above, which the server reports as
   *  0 for such a row even when it names articles, so the footer gate can name
   *  the real reason it refuses. */
  inquiryIsDeadAddressOf: (framework: string) => boolean
  /** Questions collected for the framework but not sent yet (0 when none).
   *  The review mask's note names the count; a draft never blocks the
   *  conform gesture on its own (`conformBlockReason`). */
  draftedQuestionCountOf: (framework: string) => number
  /** The declaration's whole resolved article pool (GET .../scope, `articlePool`
   *  falling back to `candidateArticles` — mirrors v1 StudioScreenB's own `articles`
   *  memo exactly). ONE pool for the whole declaration, not per-Verordnung: the data
   *  model has no framework-scoped pool, so a Geltung "Alle Artikel" write and the
   *  article slot's row list both read this same set. */
  pool: CandidateArticle[]
  poolNumbers: string[]
  /** MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): GET .../scope's issuer proposal,
   *  ONE per declaration. Null with the flag off (or no proposal on the wire),
   *  so every surface that reads it renders exactly what main renders. */
  issuerProposal?: IssuerScopeProposal | null
  /** `readModel?.uncovered_claims` keyed by article NUMBER — a supplier's
   *  advisory non-compliant claim in a table column no tracked framework
   *  covers. Never a verdict, never gates anything; the review mask surfaces
   *  it as a quiet note only. Empty when the read model carries none. */
  uncoveredClaimsByArticle: Map<string, UncoveredClaim[]>
  /** `readModel?.reader_hints` keyed by framework, then article NUMBER: what
   *  the inquiry template reader saw on a sheet row (a row with no tick but a
   *  disclosure, a merged tick, "mit Ausnahme" with nothing disclosed, a
   *  differing list edition). Advisory only, never gates anything; the review
   *  mask shows it as a quiet note (`readerHintNote`). Empty when the read
   *  model carries none. */
  readerHintsByFramework: Map<string, Map<string, ReaderHint[]>>
  /** MC_DECL_V2_SUP — from `GET .../extraction`, read INDEPENDENTLY of
   *  `isLoading`/`isError` below for the same reason inquiries are (see that
   *  field's own comment): a slow or failed extraction read must never blank
   *  the whole workspace. `null` covers BOTH "still loading" and "no read
   *  yet" — the chip/gate surfaces simply stay quiet until this resolves,
   *  same as `inquiryStateOf` defaulting to "none" while its own read is out. */
  supplierMatched: boolean | null
  supplierId: string | null
  /** The document's own verbatim issuer name (`DeclarationExtractionOut.
   *  supplierMentioned`) — present even while unmatched. The create-supplier
   *  form's only source for a Name prefill. */
  supplierMentioned: string | null
  /** Turnus Memory (#2): which supplier this printed name was resolved to before, or
   *  null when memory has nothing to say — which is the normal case. */
  memorySupplier: MemorySupplierHint | null
  /** MC_SUPPLIER_MATCH_STRICT: similar registry names for the gate, `[]` when none. */
  supplierSuggestions: SupplierNameSuggestion[]
  url: StudioV2Url
  isLoading: boolean
  isError: boolean
  /** STEP C3-b2: this page mount reads one regulation at a time (fixed per mount).
   *  Absent / false: today's full reads. */
  paged?: boolean
  /** Paged only: the navigator's own reads (`/open-items`) and the mask's (the open
   *  regulation's slice) land separately, so each pane leaves its skeleton on its own.
   *  Absent: both follow `isLoading`, as today. */
  navigatorLoading?: boolean
  maskLoading?: boolean
  /** Paged only: a pane's read failed after the page was already on screen. The pane
   *  shows an error with a retry instead of a skeleton; the rest of the page stays. */
  navigatorError?: boolean
  maskError?: boolean
  /** Paged only: the open regulation's refetch failed while older data stays on
   *  screen. The mask shows a small "could not refresh" line with a retry; the
   *  server's open items stand for that regulation until it refreshes. */
  maskRefreshError?: boolean
  retryNavigator?: () => void
  retryMask?: () => void
  /** Step C4's paged article tables, as fixed at this page mount (`MC_REVIEW_PAGED`). */
  pagedTables?: boolean
  /** Every regulation's statements in CARD ORDER, as id / group index / excluded —
   *  what the labels ("Aussage N"), Weiter's card order, the mark targets and the
   *  "all excluded" sentence read. Paged: the open regulation from its cards, the
   *  others from the server's summary (same card order, C3-s). Absent: derived from
   *  `statementCardsByFramework`, as today. */
  cardRefsByFramework?: Map<string, CardRef[]>
  /** STEP C3-b3, paged only: what a gesture that loads another regulation
   *  (`loadFrameworkState`) needs to derive it exactly as the open one — the
   *  session's own marks and the statements carrying an unsent question. */
  pendingIssues?: Record<string, string[]>
  draftedStatements?: ReadonlySet<string>
  /** Paged only: a statement's verdict from the server's summary, for a regulation
   *  whose cards are not loaded. */
  summaryVerdictOf?: (statementId: string) => Verdict | undefined
  /** Paged only: the supplier pool (`/scope`) has not landed yet — the article picker
   *  shows that it is loading instead of an empty pool. */
  poolLoading?: boolean
  /** Paged only: the supplier pool (`/scope`) read failed — the article picker shows
   *  an error with a retry instead of an empty pool. */
  poolError?: boolean
  /** Paged only: reads the supplier pool (`/scope`) again. */
  retryPool?: () => void
  /** Paged only: the stored version (write counter) the server's open items were
   *  read from. A regulation loaded for a gesture must not be older (C3-b3). */
  summaryWriteSeq?: number | null
  /** Paged only: what the R1-R4 cascade reads per regulation (a drafted question
   *  counts as "drafted"), so a regulation loaded for a gesture derives exactly as
   *  the open one does. */
  cascadeInquiryStateOf?: (framework: string) => InquiryState
}

/** `framework:groupIndex` → the matching StatementGroupEntry, keyed for O(1)
 *  lookup by BOTH the statement id (the normal case) and the ordinal fallback
 *  (a legacy row bridged only by position — see `flowState.ts::buildStateContext`,
 *  which drops entries with no `statementId` before `ctx.statements` ever sees
 *  them, so an id lookup should always hit; the ordinal is a safety net, not
 *  the expected path). */
function indexStatementGroups(groups: StatementGroup[]): {
  byId: Map<string, StatementGroupEntry>
  byOrdinal: Map<string, StatementGroupEntry>
} {
  const byId = new Map<string, StatementGroupEntry>()
  const byOrdinal = new Map<string, StatementGroupEntry>()
  for (const g of groups) {
    g.groups.forEach((entry, ordinal) => {
      if (entry.statementId) byId.set(entry.statementId, entry)
      byOrdinal.set(scopeKey(g.frameworkSlug, ordinal), entry)
    })
  }
  return { byId, byOrdinal }
}

/** Where a card sits in the document as (surface, row, column): a PDF quote's
 *  page, else its first workbook cell (`cells` is already in document order,
 *  `orderCellProofs`). Null when it cites neither. */
export function cardPosition(
  card: StatementCard,
): [number, number, number] | null {
  if (card.evidencePage !== null) return [card.evidencePage, 0, 0]
  const cell = card.cells?.[0]
  const range = cell ? parseA1Range(cell.ref) : null
  return cell && range ? [cell.page, range.startRow, range.startColumn] : null
}

// Re-exported for `mapping/frameworkSummary` (C3-s), which reads it from here.
export { comparePosition }

/** Frameworks by the earliest place any of their statements cites, then by
 *  displayed name; a framework citing nothing sorts after every one that does. */
export function documentOrder(
  frameworks: readonly string[],
  cards: ReadonlyMap<string, readonly StatementCard[]>,
  nameOf: (framework: string) => string,
): string[] {
  const first = new Map(
    frameworks.map((fw) => [
      fw,
      (cards.get(fw) ?? [])
        .map(cardPosition)
        .reduce<
          [number, number, number] | null
        >((min, p) => (comparePosition(p, min) < 0 ? p : min), null),
    ]),
  )
  return [...frameworks].sort(
    (a, b) =>
      comparePosition(first.get(a) ?? null, first.get(b) ?? null) ||
      nameOf(a).localeCompare(nameOf(b)),
  )
}

/** Ordered unique framework slugs, first-seen in `ctx.statements`. */
function orderedFrameworks(ctx: StateContext): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const s of ctx.statements) {
    if (!seen.has(s.framework)) {
      seen.add(s.framework)
      out.push(s.framework)
    }
  }
  return out
}

/** A statement joined for display: everything `UnitFacts` needs, plus the
 *  document-facing basis text the read-only unit card quotes. */
export interface StatementCard {
  id: string
  framework: string
  groupIndex: number
  verdict: Verdict | null
  scopeAll: boolean
  articleNumbers: string[]
  unresolvedRefs: number
  /** The statement's own scope wording, as extracted — "Aussage laut Dokument". */
  scopeVerbatim: string | null
  conditions: StatementCondition[]
  /** The FRAMEWORK's `condition_fill.validation` findings tagged `data_gap`
   *  (2026-09-04 product decision). Framework-wide on purpose, although it
   *  hangs off one card: a finding is addressed by (framework, article,
   *  substance) — a CELL, never a statement — and every reader looks it up
   *  by that address for an article of THIS card's own `editorArticles`
   *  (`openDataGap`/`concentrationRequired` via `openCounts`, whose article
   *  axis is `entryArticles`, always a subset of `editorArticles`). So a
   *  finding on an article outside this statement's scope can never be
   *  read, and one on an article two statements share belongs to both:
   *  narrowing this list per statement would change nothing. Do not "fix"
   *  it into a statement filter; fix the address if that ever changes.
   *  RAW, not yet healed against the live
   *  condition rows (`SubstanceEditor` does that itself with its own
   *  fresh `rows`, via the shared `findingHealed`, so the calm indicator clears
   *  the instant the reviewer fills the field, same as the red state always
   *  has). Never drives `requiredIncomplete`/approve-gate blocking — see
   *  `flowState.ts`'s own skip for that. Optional (defaults to `[]`) so every
   *  hand-built test fixture predating this field keeps compiling. */
  conditionDataGaps?: ConditionFillFinding[]
  /** The FRAMEWORK's named slots (the read model's `named_slots`, here keyed by
   *  article number): the bare cas-or-id(s) a reviewer named a nameless slot
   *  with (the substance pick, TUR-1725), which `conditions` still lists as
   *  nameless. Framework-wide for the reason `conditionDataGaps` is: the editor
   *  reads it only for this card's own nameless slot (`withNamedSlots`).
   *  Optional: absent means none. */
  namedSlots?: ReadonlyMap<string, readonly string[]>
  /** At least one collected, unsent question names this statement (the W5-1
   *  collector). Drives the derived Geltung "ask" on the card and the
   *  framework's `drafted` inquiry state, so the Rückfrage reading survives a
   *  sheet, a regulation switch and a reload (live test, wave 5). Optional
   *  so hand-built fixtures keep compiling; absent means false. */
  draftQuestions?: boolean
  /** What keeps this statement's step-3 issue open (`openPointDetail`), off
   *  the same override-adjusted worklist the gate reads. */
  openDetail?: OpenPointDetail | null
  /** The listVersion an edit should carry alongside a new verdict, so a
   *  verdict-only change never blanks a reference list nobody touched — see
   *  `effectiveListVersion`. */
  listVersion: string | null
  /** TUR-1927: the stored verdict override this screen holds (null: none), sent
   *  with a verdict edit so the server refuses it when another tab or colleague
   *  changed the verdict since. Undefined (no statement entry, a hand-built
   *  fixture): not sent, the edit is unchecked as before. */
  verdictOverride?: string | null
  /** The concrete articles this statement's scope resolves to right now: M2's
   *  resolution for a scopeAll statement, else its own persisted subset. What
   *  the condition-field editor iterates (never the whole declaration pool —
   *  a scopeAll statement over an unresolved framework has no bound article
   *  set to iterate at all, hence the `?? []`). */
  resolvedArticles: string[]
  /** The articles the condition/substance EDITOR iterates (`SubstanceEditor`,
   *  `SubstanceRow`'s chips, the legacy `ConditionFieldsEditor`). Equals
   *  `resolvedArticles` in every case but one: a scopeAll statement whose M2
   *  resolution is EMPTY. The approve gate (`flowState.ts`, twin
   *  `mc_declaration_cell_state.py`) covers such a statement with its
   *  `selectedArticles` (its explicit subset if it has one, which this then
   *  equals, else the whole supplier pool) and raises needs-input on the
   *  ones carrying a blocking `condition_fill.validation` finding, while
   *  `resolvedArticles` stays `[]`
   *  (rule R2, the approved snapshot, the counts and the article sheet all
   *  read "empty = not resolved"), so the editor had nothing to render and
   *  the reviewer could not fill what the gate demanded (N9; LQ / Hornung
   *  2026-09-09, see `approvedSnapshot.ts`). For the pool case this is
   *  `poolFallbackArticles`: the gate's own coverage set, in its order,
   *  narrowed to the articles with a non-`data_gap` finding of this card's
   *  framework; never the whole pool (a 10k-article pool would render 10k
   *  rows). It is a SUPERSET of the gate's needs-input: it also lists
   *  findings the gate skips (a cell whose verdict no longer needs condition
   *  data) and healed findings, so the reviewer still sees what they filled;
   *  whether a finding still blocks is the gate's own decision. The live
   *  verdict-basis loop in `buildStateContext` walks the same set for such a
   *  statement. Optional so hand-built test fixtures keep compiling;
   *  consumers read `card.editorArticles ?? card.resolvedArticles`. */
  editorArticles?: string[]
  /** The FIRST evidence anchor's page (`entry.evidence[0].page`) — the
   *  document-facing page-jump button's target. Null when the statement
   *  carries no quote-addressed evidence at all (a workbook statement, or a
   *  legacy run with none): the button hides rather than jumping nowhere. */
  evidencePage: number | null
  /** The statement's FULL evidence anchors (`entry.evidence`, verbatim) —
   *  what a unit card's page-chip/header click flashes in the document pane,
   *  via the SAME `highlight`/`sources` viewer props v1's `showAnchor`
   *  already used (see `StudioV2Layout::handleJumpToEvidence`). `evidencePage`
   *  above is a derived convenience (this array's first page) kept for the
   *  chip's own label; this is the superset it was derived from. Empty on a
   *  workbook statement or a legacy run with no quote-addressed evidence. */
  evidence: EvidenceAnchor[]
  /** The statement's own review status is "rejected" (the staged row's
   *  `statementStatus` — the SAME source `flowState.ts`'s override bucket
   *  reads to derive "excluded", and `RemoveStatementControl`'s one-way
   *  "Aussage entfernen" gesture writes). The card still renders (dropping
   *  it would silently break `cards[index]` numbering for every OTHER
   *  card), but as a quiet, visibly-excluded entry — never indistinguishable
   *  from a live one. Never a client-only flag: derived from `entry.
   *  statementStatus`, the same wire field `buildOverrideViews` keys off. */
  excluded: boolean
  /** A reviewer has acted on this statement: edited its verdict (`verdictOverride`),
   *  signed it in step 1 (`confirmedValues`), or verified one of its selected
   *  articles in step 3 (a `__verified__` mark for its framework, read off
   *  `ctx.verified`, the classifier's own view). Those are the three things the
   *  reset undoes, so it is what gates the card's "reset to extracted" gesture: a
   *  bare extracted statement has nothing to reset. Scope and list-version edits
   *  are not recorded here. False when no entry joins and no mark exists.
   *  Optional for the same fixture-compat reason as `evidenceGap`/`cells`
   *  above (twenty hand-built `StatementCard` fixtures predate it);
   *  `statementCardsByFramework` below always sets it, absent reads false. */
  reviewerTouched?: boolean
  /** The statement's cell anchors (`StatementGroupEntry.cells`, verbatim) —
   *  a WORKBOOK statement's own evidence channel: `to_evidence` excludes
   *  cells server-side (see `CellQuote`'s own doc), so `evidence`/
   *  `evidencePage` above are always empty for a workbook statement and this
   *  is the only channel that carries anything. Empty (or absent — see
   *  below) on a PDF declaration, or a workbook statement with no cell
   *  evidence at all — either way the honest "nothing to jump to" the unit
   *  card's own affordance gates on.
   *
   *  OPTIONAL, not because a real card ever omits it (`statementCardsByFramework`
   *  below always sets it, `?? []` included), but so every OTHER test file's
   *  own hand-built `StatementCard` fixture that predates this field — none
   *  of them live in this pass's allowed file set — keeps compiling and
   *  passing unchanged. Consumers read it the same way regardless
   *  (`card.cells ?? []`). */
  cells?: CellQuote[]
  /**
   * The read model's `scope_evidence_gaps` entry for this statement
   * (looked up by `scopeKey(framework, groupIndex)`), or null/absent when it
   * carries none. Set exactly when the binder's blanket-seed carve-out fired
   * server-side: the statement's article set is already bound (a SYSTEM
   * binding, no `needs_input`), but its seed clause never verified a quote —
   * an evidence-quality WARNING, never a block. `UnitCard` renders it as a
   * quiet amber note in the ARTIKEL slot.
   *
   * OPTIONAL for the same reason `cells` above is: every OTHER test file's own
   * hand-built `StatementCard` fixture predates this field and must keep
   * compiling unchanged. `statementCardsByFramework` below always sets it.
   */
  evidenceGap?: { cause: string; articles: number } | null
  /** This statement was authored by a reviewer, not staged from the extraction
   *  (TUR-1445). Server-derived from the row's creator — durable where the
   *  `included` override note is not (an `excluded` note supersedes it for
   *  good). The card wears it as a quiet "Manuell ergänzt" chip. Optional for
   *  the same fixture-compat reason as `evidenceGap` above. */
  manuallyAdded?: boolean
  /** References the document names that M2 already resolved to a live
   *  article (TUR-1211 wave 3, product decision): the covered counterpart
   *  to `unresolvedRefs` above, never a gap. `UnitCard` renders it as a
   *  quiet note next to the Geltung line. Optional for the same
   *  fixture-compat reason as `evidenceGap`/`cells` above: every OTHER test
   *  file's own hand-built `StatementCard` fixture predates this field and
   *  must keep compiling unchanged; `statementCardsByFramework` below
   *  always sets it. */
  coveredRefs?: CoveredRef[]
  /** What the resolver raised about its OWN work on this statement's scope,
   *  as data (`ScopeFlagFact`): references it was never shown, ones it could
   *  not attribute, ones whose annex or row claim disagrees. The article row
   *  renders each as a quiet note beside the covered-refs one; a kind this
   *  build does not know renders nothing (see `ScopeFlagFact`'s own doc on
   *  why the union is open). Optional for the same fixture-compat reason as
   *  `coveredRefs` above. */
  scopeFlags?: ScopeFlagFact[]
  /** A verdict or scope edit on THIS statement is in flight (TUR-1211, W5-5):
   *  copied verbatim from the statements-query entry's own `pending`
   *  (the optimistic patch `declarationsApi.ts::patchStatement` sets), never
   *  a client-only flag of this hook's own: one source, so a rollback on a
   *  failed save clears it exactly when the cache entry it came from
   *  reverts. `UnitCard` reads it for `aria-busy` on the verdict/scope
   *  controls; it does not add its own disabled state (`mutation.isPending`
   *  already covers that). Optional/default false for the same fixture-
   *  compat reason as `evidenceGap`/`cells`/`coveredRefs` above. */
  saving?: boolean
  /** The document references M2 could NOT bind (`scope_resolutions[key].
   *  unresolved`, the structured shape, else the bare `unresolved_refs`
   *  strings): the article browser offers each as a search suggestion.
   *  Unlike `unresolvedRefs` above this is NOT zeroed by a reviewer apply:
   *  the words stay useful as searches after the scope is bound. Optional
   *  for the same fixture-compat reason as `coveredRefs`. */
  unresolvedRefTexts?: string[]
  /** The scope resolver's own filter chain for this statement
   *  (`scope_resolutions[key].chain`, see `ScopeChain`): the article
   *  browser offers its steps as search suggestions beside the unresolved
   *  references. Null/absent when the resolver escalated, or on an older
   *  fixture (optional for the same compat reason as `unresolvedRefTexts`). */
  scopeChain?: ScopeChain | null
  /** The staged statement's scope reason codes (`StatementGroupEntry.
   *  scopeReasons`, e.g. `family_unmatched`, `scope_empty`). The Rückfrage
   *  draft reads them to tell "the document names nothing" apart from "the
   *  document names articles that matched nothing" (TUR-1652). Optional for
   *  the same fixture-compat reason as `scopeChain` above. */
  scopeReasons?: string[]
  /** The article references the document names that matched no article
   *  (`StatementGroupEntry.unmatchedTerms`), ordered by the page the
   *  document prints them on: what the Rückfrage draft quotes back to the
   *  supplier (TUR-1652). Optional for the same fixture-compat reason as
   *  `scopeChain` above. */
  unmatchedTerms?: string[]
  /** False when the declaration's supplier pool (`GET .../scope`, the
   *  `pool` this hook exposes) holds no article at all: nothing in master
   *  data could ever match, so the Rückfrage draft must not blame the
   *  document, and AskBox tells the reviewer to import or link articles
   *  first when a supplier is matched (`supplierMatched` below) (TUR-1652).
   *  Read live, never staging's snapshot, so it clears the moment articles
   *  exist. Absent (fixtures, `statementCardsByFramework` alone) means "not
   *  known to be empty". */
  supplierHasArticles?: boolean
  /** False when `GET .../scope` says the declaration has no master-data
   *  supplier yet (its `supplierMatched`, the same response as the pool, so
   *  the two never disagree mid-refetch). The pool is then empty for that
   *  reason, not because the supplier has no articles, so AskBox's import
   *  hint stays quiet: the layout's unmatched-supplier banner already names
   *  the step. Absent means "not known". */
  supplierMatched?: boolean
}

/**
 * The listVersion an edit MUST send alongside a verdict change: `entry.values`
 * is the CURRENT persisted row — the same field `flowState.ts` reads as the
 * canonical listVersion elsewhere, and the one a list-version-only write
 * (`EditionRow`'s `useEditStatementVerdict({verdict: null, listVersion})`)
 * actually moves. It must therefore win first, or a NEWER persisted edition
 * gets shadowed by an OLDER `confirmedValues` snapshot: step-1 confirm only
 * signs verdict+listVersion at THAT moment, EditionRow's write never re-signs
 * it, so after "confirm at edition A, then switch to edition B via
 * EditionRow" the confirmed snapshot still reads A while `values` already
 * reads B — and a verdict-only edit re-posting the confirmed snapshot would
 * silently revert the reviewer's own B pick back to A.
 *
 * `confirmedValues`/`proposed` remain fallbacks ONLY for a statement whose
 * `values.listVersion` itself is null (nothing has ever set one there) — this
 * is what keeps a still-null verdict-only edit from blanking a reference list
 * the reviewer signed off in step 1 but that never made it into `values`
 * (a server older than the signature store, or a run that never had a
 * catalogue match). The reviewer's own signature still outranks the run's raw
 * proposal in that fallback chain. See EditStatementVerdictIn's doc: a null
 * verdict is documented as "leave it standing", but nothing says the same of
 * a null listVersion, so this never falls through to bare null while any of
 * the three has one.
 */
export function effectiveListVersion(
  entry: StatementGroupEntry | null,
): string | null {
  if (!entry) return null
  return (
    entry.values.listVersion ??
    entry.confirmedValues?.listVersion ??
    entry.proposed?.listVersion ??
    null
  )
}

/** One stable empty list, so a declaration without suggestions keeps the same reference. */
const NO_SUGGESTIONS: SupplierNameSuggestion[] = []

export const toUnitFacts = (card: StatementCard): UnitFacts => ({
  verdict: card.verdict,
  scopeAll: card.scopeAll,
  articleNumbers: card.articleNumbers,
  resolvedArticleNumbers: card.resolvedArticles,
  unresolvedRefs: card.unresolvedRefs,
})

/** A card's named slots: its framework's (`named_slots`, cells named for every
 *  statement), with the articles named for this statement on its own
 *  (`named_slots_by_statement`, TUR-1929) taking that statement's substances
 *  instead, by article number. */
function withStatementSlots(
  framework: ReadonlyMap<string, string[]> | undefined,
  statement: Record<string, string[]> | undefined,
): ReadonlyMap<string, readonly string[]> | undefined {
  const entries = Object.entries(statement ?? {})
  if (entries.length === 0) return framework
  const own = new Map<string, string[]>()
  for (const [bare, numbers] of entries)
    for (const n of numbers ?? []) {
      const bares = own.get(n)
      if (!bares) own.set(n, [bare])
      else if (!bares.includes(bare)) bares.push(bare)
    }
  const map = new Map<string, string[]>()
  for (const [n, bares] of framework ?? [])
    if (!own.has(n)) map.set(n, [...bares])
  for (const [n, bares] of own) map.set(n, bares)
  return map
}

/** The per-framework join `deriveFrameworkVerdict` and the unit cards both
 *  read — see the module doc for why an unmatched statement still
 *  contributes a card instead of being silently dropped: `ctx.statements` is
 *  the derivation's own account of what exists, and a declaration whose
 *  statement read briefly disagrees with it (a stale cache tick) must not
 *  make a framework vanish from the navigator. */
export function statementCardsByFramework(
  ctx: StateContext,
  statementGroups: StatementGroup[],
  readModel: DeclarationReadModel | null = null,
): Map<string, StatementCard[]> {
  const { byId, byOrdinal } = indexStatementGroups(statementGroups)
  const evidenceGaps = readModel?.scope_evidence_gaps ?? {}
  const validation = readModel?.condition_fill?.validation ?? []
  const findingArticles = blockingFindingArticles(validation)
  // Per framework, the named slots by article number (one pass, shared by the
  // framework's cards).
  const namedSlotsByFramework = new Map<string, Map<string, string[]>>()
  for (const [framework, byBare] of Object.entries(
    readModel?.named_slots ?? {},
  )) {
    const map = new Map<string, string[]>()
    for (const [bare, numbers] of Object.entries(byBare ?? {}))
      for (const n of numbers ?? []) {
        const bares = map.get(n)
        if (bares) bares.push(bare)
        else map.set(n, [bare])
      }
    if (map.size > 0) namedSlotsByFramework.set(framework, map)
  }
  const out = new Map<string, StatementCard[]>()
  for (const s of ctx.statements) {
    const entry =
      byId.get(s.id) ??
      byOrdinal.get(scopeKey(s.framework, s.groupIndex)) ??
      null
    const key = scopeKey(s.framework, s.groupIndex)
    const resolution = ctx.scopeResolutions[key]
    // Mirror classifyScope's own precedence (studio/cellState.ts, the
    // `if (i.applied || i.unresolvedCount === 0) return "confirmed"` line): a
    // reviewer-applied scope overrides unresolved refs there, so the SAME
    // decision must govern what this card displays — otherwise the display
    // and the (correct) worklist gate permanently disagree: R1 would read
    // "Rückfrage nötig" forever after the reviewer already applied. This is
    // that same decision, rendered for display — keep the two in lockstep.
    const unresolvedRefs =
      ctx.applied[key] === true ? 0 : (resolution?.unresolved_refs?.length ?? 0)
    const scopeAll = entry?.scopeAll ?? false
    const articleNumbers = entry?.articleNumbers ?? s.selectedArticles
    const resolvedArticles = scopeAll
      ? (resolution?.article_numbers ?? [])
      : articleNumbers
    const card: StatementCard = {
      id: s.id,
      framework: s.framework,
      groupIndex: s.groupIndex,
      verdict: s.verdict,
      scopeAll,
      articleNumbers,
      unresolvedRefs,
      // Same source `flowState.ts::buildOverrideViews` reads (`statusOf.get(s.id)
      // === "rejected"`) — the staged row's own review status, never a client flag.
      excluded: entry?.statementStatus === "rejected",
      // The marks are the classifier's own view (`ctx.verified`, keyed
      // framework|article), not the entry's: the footer's verify gesture writes
      // them with no signature, and a reset drops exactly these.
      reviewerTouched:
        entry?.verdictOverride != null ||
        entry?.confirmedValues != null ||
        s.selectedArticles.some(
          (a) => ctx.verified[verifiedKey(s.framework, a)] === true,
        ),
      scopeVerbatim: entry?.values.scopeVerbatim ?? null,
      conditions: entry?.values.conditions ?? [],
      listVersion: effectiveListVersion(entry),
      verdictOverride: entry ? (entry.verdictOverride ?? null) : undefined,
      evidencePage: entry?.evidence?.[0]?.page ?? null,
      evidence: entry?.evidence ?? [],
      // The first cell is what the card jumps to: the answer sheet first (the
      // model may have cited a legal-text tab first), then the first cell
      // tagged `verdict` (it may have cited a header first).
      cells: orderCellProofs(entry?.cells ?? [], entry?.taggedEvidence ?? []),
      evidenceGap: evidenceGaps[key] ?? null,
      // Server-derived provenance, not the in-force note: the `included` note
      // is a single mutable slot that removal's `excluded` note supersedes.
      manuallyAdded: entry?.manuallyAdded === true,
      saving: entry?.pending === true,
      coveredRefs: resolution?.covered_refs ?? [],
      // The structured shape lives on the read model only (`ctx`'s own
      // resolution copy carries the bare strings), hence the two sources.
      unresolvedRefTexts:
        readModel?.scope_resolutions?.[key]?.unresolved?.map((u) => u.text) ??
        resolution?.unresolved_refs ??
        [],
      scopeChain: readModel?.scope_resolutions?.[key]?.chain ?? null,
      // Read off the READ MODEL, like `scopeChain` and `unresolvedRefTexts`
      // above: `ctx`'s own resolution copy carries the article/ref lists
      // only, never the resolver's own account of its work.
      scopeFlags: readModel?.scope_resolutions?.[key]?.flag_facts ?? [],
      scopeReasons: entry?.scopeReasons ?? [],
      unmatchedTerms: entry?.unmatchedTerms ?? [],
      resolvedArticles,
      editorArticles: !(scopeAll && resolvedArticles.length === 0)
        ? resolvedArticles
        : articleNumbers.length > 0
          ? // An explicit subset: the gate covers exactly it, and it is
            // bounded, so the editor offers all of it.
            articleNumbers
          : poolFallbackArticles(
              s.selectedArticles,
              s.framework,
              findingArticles,
            ),
      conditionDataGaps: validation.filter(
        (f) => f.framework === s.framework && f.data_gap === true,
      ),
      namedSlots: withStatementSlots(
        namedSlotsByFramework.get(s.framework),
        readModel?.named_slots_by_statement?.[key],
      ),
    }
    const list = out.get(s.framework)
    if (list) list.push(card)
    else out.set(s.framework, [card])
  }
  // DOCUMENT ORDER (TUR-1211): the pipeline merges statements by (verdict,
  // list_version) — merge.py, order-blind by design — so the read model's
  // order is merge order, not the page order a reviewer reads the PDF in.
  // Sort each framework's cards by the page of their first evidence anchor;
  // cards with no page keep their relative read-model order at the end.
  // `groupIndex`/`group_ordinal` are untouched: they address the staged row.
  for (const [fw, cards] of out) {
    out.set(
      fw,
      cards
        .map((card, index) => ({ card, index }))
        .sort(
          (a, b) =>
            (a.card.evidencePage ?? Number.POSITIVE_INFINITY) -
              (b.card.evidencePage ?? Number.POSITIVE_INFINITY) ||
            a.index - b.index,
        )
        .map(({ card }) => card),
    )
  }
  return out
}

type Translate = ReturnType<typeof useTranslation>["translate"]

/** A FastAPI default 404 (`{"detail": "Not Found"}`): the route itself is missing —
 *  an older backend during a rolling deploy. `FRAMEWORK_NOT_FOUND` (a structured
 *  detail) is a different thing: the route exists, the regulation does not. */
function isRouteMissing(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 404) return false
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === "string"
}

function isFrameworkNotFound(error: unknown): boolean {
  return (
    getApiErrorStatus(error) === 404 &&
    getApiErrorCode(error) === "FRAMEWORK_NOT_FOUND"
  )
}

interface PagedSource {
  /** The page reads the slice. False once a fallback fired (old backend, the slice
   *  route missing, `/open-items` failed before answering): today's full reads. */
  sliceMode: boolean
  /** Something of the paged page (the summary or the open slice) has been on screen:
   *  from here on the source never changes, and failures are per-pane errors. */
  committed: boolean
  /** The summary cannot be had any more (failed, or an older backend answered after
   *  the page was already on screen). */
  summaryError: boolean
  /** On screen, the latest answer came without a summary (it failed on the server, or
   *  an older pod answered): the last summary is kept, the footer reads unknown. */
  summaryMissing: boolean
  retrySlice: () => void
  activeFramework: string | null
  slice: DeclarationFrameworkSlice | undefined
  sliceLoading: boolean
  sliceError: boolean
  /** The open slice is on screen but may be behind the server (its version differs
   *  from the summary's, or its last refetch failed): its own count must not stand. */
  sliceUntrusted: boolean
  /** The open slice's last refetch failed; older data is still shown. */
  sliceRefreshFailed: boolean
  /** The open regulation's slice has landed (success): background reads may start. */
  sliceSettled: boolean
  summary: FrameworkSummaryWire[] | null
  /** The regulations in document order, from the summary. */
  order: string[]
  collisions: Map<string, number | null>
}

/**
 * STEP C3-b2: WHICH REGULATION IS OPEN, AND ITS SLICE.
 *
 * - The open regulation: the URL's `sfw` when the summary lists it (or while the
 *   summary is still on its way — so a reload or a shared link fetches `/open-items`
 *   and the slice IN PARALLEL), else the regulation holding the URL's `stmt` (old
 *   links name a statement only), else the first in document order. `sfw` is then
 *   written into the URL, so the next load is parallel too.
 * - A stale `sfw` (`FRAMEWORK_NOT_FOUND`, or absent from the summary) lands on the
 *   first regulation.
 * - FALLBACK to today's full reads, for the rest of the mount: `/open-items` without
 *   `frameworks` (a backend older than C3-s), the slice route missing (a default 404,
 *   rolling deploy), or `/open-items` failing before it ever answered. The fallback
 *   can only fire before the mask has rendered (it needs a slice or a summary to
 *   render), so no editor ever sees its source change.
 * - PREFETCH: once the open slice has landed, the next regulation in document order.
 */
function usePagedSource(
  declId: string,
  url: StudioV2Url,
  openItemsQ: {
    data: DeclarationOpenItemsWire | undefined
    isError: boolean
  },
  translate: Translate,
  fellBack: boolean,
  setFellBack: (value: boolean) => void,
): PagedSource {
  // Only the prefetch uses it; read without `useQueryClient` (which throws outside a
  // provider) so the state hook stays renderable on its own.
  const queryClient = useContext(QueryClientContext)
  const openItems = openItemsQ.data
  const oldBackend = !!openItems && isOldOpenItemsBackend(openItems)
  const openItemsFailed = openItemsQ.isError && !openItems
  const answeredSummary =
    openItems && !oldBackend ? (openItems.frameworks ?? null) : null
  // The last summary the server gave, kept for the rest of the mount: once the page is
  // on screen, an answer without `frameworks` (the summary failed on the server, or an
  // older pod answered during a rolling deploy) must not shrink the navigator to the
  // open regulation — it keeps its list, and the footer reads the answer as unknown.
  const [lastSummary, setLastSummary] = useState<FrameworkSummaryWire[] | null>(
    null,
  )
  if (answeredSummary && answeredSummary !== lastSummary)
    setLastSummary(answeredSummary)
  // Latched below once something of the paged page is on screen (see THE SOURCE IS
  // DECIDED ONCE).
  const [committed, setCommitted] = useState(false)
  const summary = answeredSummary ?? (committed ? lastSummary : null)
  // On screen, the server answered without a summary: its issues still stand, but the
  // regulations it cannot describe read as unknown — never green, Approve held.
  const summaryMissing = committed && !!openItems && answeredSummary === null
  const nameOf = useMemo(() => {
    const names = new Map(
      (summary ?? []).map((f) => [f.framework, f.regulation_name ?? null]),
    )
    return (fw: string) => frameworkDisplayName(fw, names, translate)
  }, [summary, translate])
  const order = useMemo(
    () => (summary ? documentOrderFromSummary(summary, nameOf) : []),
    [summary, nameOf],
  )
  const collisions = useMemo(
    () => collisionsFromSummary(summary ?? []),
    [summary],
  )
  const wanted = url.state.framework
  const stmt = url.state.statementId
  // The regulation holding the link's statement, once the summary names it.
  const holder =
    summary && stmt
      ? summary.find((f) => f.statements.some((s) => s.statement_id === stmt))
          ?.framework
      : undefined
  let activeFramework: string | null
  if (!summary) activeFramework = wanted ?? null
  else if (wanted && order.includes(wanted)) activeFramework = wanted
  else activeFramework = holder ?? order[0] ?? null
  // THE SOURCE IS DECIDED ONCE, BEFORE THE FIRST PAINT. As soon as the summary or the
  // open regulation's slice has landed, the page is "committed" to the slices for the
  // rest of the mount: a later failure (a rolling deploy answering from an old pod, a
  // 500, a regulation the user switches to) becomes that pane's error with a retry,
  // never a swap to the full reads under an open editor.
  const sliceQ = useDeclarationFramework(declId, activeFramework, {
    enabled: !fellBack && !(oldBackend && !committed),
  })
  const routeMissing = sliceQ.isError && isRouteMissing(sliceQ.error)
  const staleFramework = sliceQ.isError && isFrameworkNotFound(sliceQ.error)
  // Something the paged page can paint: the summary, or the open regulation's slice
  // (a reload with `sfw` whose `/open-items` failed still shows that regulation, with
  // the navigator's error beside it). An older backend before the first paint still
  // falls back, slice or not: the full reads serve the whole page, a summary never comes.
  const paintable = summary !== null || !!sliceQ.data
  const fallBack =
    !committed &&
    (oldBackend || routeMissing || (openItemsFailed && !paintable))
  // Both latched for the rest of the mount (React's "adjust state during render"
  // pattern: one-way flips, so each settles after one extra render).
  if (fallBack && !fellBack) setFellBack(true)
  const sliceMode = !fellBack && !fallBack
  if (sliceMode && !committed && paintable) setCommitted(true)
  const queryClientForRetry = queryClient
  // `sfw` into the URL once known, so the next load fetches both reads at once.
  // A correction, not a switch: a link whose `sfw` this declaration lacks opens
  // the regulation holding its `stmt`, so the patch names `stmt` and `step` to
  // keep them. A `stmt` no regulation holds is left unnamed, and
  // applyStudioV2Patch drops it with its `step` when a stale `sfw` is replaced.
  const { patch } = url
  const keepTarget = holder !== undefined && holder === activeFramework
  useEffect(() => {
    if (!sliceMode || !summary || !activeFramework) return
    if (url.state.framework !== activeFramework)
      patch({
        framework: activeFramework,
        ...(keepTarget
          ? { statementId: url.state.statementId, step: url.state.step }
          : {}),
      })
  }, [
    sliceMode,
    summary,
    activeFramework,
    keepTarget,
    url.state.framework,
    url.state.statementId,
    url.state.step,
    patch,
  ])
  // Prefetch the next regulation only after the open one has landed.
  const sliceSettled = sliceQ.isSuccess
  const next =
    activeFramework && order.length > 1
      ? order[(order.indexOf(activeFramework) + 1) % order.length]
      : null
  useEffect(() => {
    if (!sliceMode || !sliceSettled || !next || next === activeFramework) return
    if (queryClient) void prefetchFrameworkSlice(queryClient, declId, next)
  }, [sliceMode, sliceSettled, next, activeFramework, queryClient, declId])
  // REVISION CHECK (design §3.2): the slice and the summary say which stored version
  // they were read from. When both are known and differ, the older one is refetched,
  // once per pair, so the open regulation and the others never sit on different
  // versions after a write that landed between the two reads.
  const sliceSeq = sliceQ.data?.revision?.writeSeq ?? null
  const summarySeq = openItems?.revision?.write_seq ?? null
  const reconciled = useRef<string | null>(null)
  const refetchSlice = sliceQ.refetch
  useEffect(() => {
    if (!sliceMode || sliceSeq === null || summarySeq === null) return
    if (sliceSeq === summarySeq) return
    const pair = `${activeFramework}:${sliceSeq}:${summarySeq}`
    if (reconciled.current === pair) return
    reconciled.current = pair
    if (!queryClientForRetry || !activeFramework) return
    // An invalidation, not a plain refetch: the footer's "refreshing" counts
    // invalidated reads, so Approve waits for this one like for any other.
    if (sliceSeq < summarySeq)
      void queryClientForRetry.invalidateQueries({
        queryKey: frameworkSliceKey(declId, activeFramework),
        exact: true,
      })
    else refreshOpenItems(queryClientForRetry, declId)
  }, [
    sliceMode,
    sliceSeq,
    summarySeq,
    activeFramework,
    queryClientForRetry,
    declId,
  ])
  // The open regulation's slice and the summary were read from different stored
  // versions: until they agree again, the slice may hold a state the server has
  // already moved past (a write elsewhere changed this regulation too: shared
  // framework-less rows, the supplier pool), so its own count must not stand.
  const sliceVersionBehind =
    sliceMode &&
    sliceSeq !== null &&
    summarySeq !== null &&
    sliceSeq !== summarySeq
  // A refetch failed while older data is still on screen: shown, but not trusted.
  const sliceRefreshFailed = sliceQ.isError && !!sliceQ.data
  // A stale `sfw` (`FRAMEWORK_NOT_FOUND`) while the summary is still out is not an
  // error: the summary moves the page to the first regulation. Once the summary has
  // answered and still lists it, it IS one (the summary is behind): retry offered.
  const staleWhileWaiting = staleFramework && !summary
  const retrySlice = useCallback(() => {
    void refetchSlice()
    if (staleFramework && queryClientForRetry)
      refreshOpenItems(queryClientForRetry, declId)
  }, [refetchSlice, staleFramework, queryClientForRetry, declId])
  // A failed REFETCH keeps the data on screen: only a slice that never landed is an
  // error. A missing route before the page committed is a fallback, not an error.
  const sliceFailed = sliceQ.isError && !sliceQ.data
  return {
    sliceMode,
    committed,
    activeFramework,
    slice: sliceQ.data,
    sliceLoading: sliceQ.isLoading || (staleWhileWaiting && !sliceQ.data),
    sliceError:
      sliceFailed && !staleWhileWaiting && !(routeMissing && !committed),
    sliceUntrusted: sliceVersionBehind || sliceRefreshFailed,
    sliceRefreshFailed,
    sliceSettled,
    summary,
    summaryError: committed && !summary && (openItemsFailed || oldBackend),
    summaryMissing,
    retrySlice,
    order,
    collisions,
  }
}

/** The inputs the paged state is built from: the common reads `useStudioV2State`
 *  already prepared, plus the paged source. */
interface PagedStateInput {
  declId: string
  flow: ReturnType<typeof useDeclarationFlowState>
  translate: Translate
  url: StudioV2Url
  isLoading: boolean
  isError: boolean
  navigatorLoading: boolean
  navigatorError: boolean
  maskError: boolean
  /** The open slice may be behind the server: the server's open items stand for it. */
  maskUntrusted: boolean
  /** The open slice's last refetch failed (older data still shown). */
  maskRefreshFailed: boolean
  retryNavigator: () => void
  retryMask: () => void
  status: OpenItemsStatus | null
  openItemsData: DeclarationOpenItemsWire | undefined
  /** "unknown" only because the answer came without a summary: its issues are still
   *  the server's word and are merged (the regulations still read unknown). */
  serverIssuesStand: boolean
  slice: DeclarationFrameworkSlice | undefined
  summary: FrameworkSummaryWire[] | null
  order: string[]
  activeFramework: string | null
  pool: CandidateArticle[]
  poolLoading: boolean
  poolError: boolean
  retryPool: () => void
  draftedStatements: Set<string>
  cascadeInquiryStateOf: (framework: string) => InquiryState
  supplierMatched: boolean | null
  supplierId: string | null
  supplierMentioned: string | null
  memorySupplier: MemorySupplierHint | null
  supplierSuggestions: SupplierNameSuggestion[]
  /** Null with MC_ISSUER_SCOPE_PROPOSAL off, as flag off builds it. */
  issuerProposal: IssuerScopeProposal | null
  uncoveredClaimsByArticle: Map<string, UncoveredClaim[]>
  readerHintsByFramework: Map<string, Map<string, ReaderHint[]>>
  inquiryStateOf: StudioV2State["inquiryStateOf"]
  inquiryArticleCountOf: StudioV2State["inquiryArticleCountOf"]
  inquiryIsDeadAddressOf: StudioV2State["inquiryIsDeadAddressOf"]
  draftedQuestionCountOf: StudioV2State["draftedQuestionCountOf"]
}

/**
 * STEP C3-b2: THE PAGE STATE FROM ONE REGULATION'S SLICE + THE SERVER'S SUMMARY.
 *
 * The open regulation is built exactly as flag off builds it (the same
 * `statementCardsByFramework`, `openPointDetail`, `deriveFrameworkVerdict`, over the
 * same `deriveOpenWork` context, fed the slice). Every other regulation comes from the
 * summary: names, document order, derivation (`deriveFromSummary`), card order. The
 * worklist is C1-d's merge: the server's issues for the others, the browser's for the
 * open one.
 */
function pagedStudioState(input: PagedStateInput): StudioV2State {
  const { flow, summary, activeFramework, slice, status, openItemsData } = input
  const regulationNameByFramework = new Map<string, string | null>(
    (summary ?? []).map((f) => [f.framework, f.regulation_name ?? null]),
  )
  if (slice)
    regulationNameByFramework.set(
      slice.framework,
      slice.statements.regulationName ??
        regulationNameByFramework.get(slice.framework) ??
        null,
    )
  const frameworks = summary
    ? input.order
    : activeFramework
      ? [activeFramework]
      : []
  const ctx = flow.ctx
  const cards = ctx
    ? statementCardsByFramework(
        ctx,
        slice ? [slice.statements] : [],
        flow.readModel,
      )
    : new Map<string, StatementCard[]>()
  const stepThreeIssues = new Map(
    flow.worklist
      .filter((i) => i.step === 3 && i.statementId)
      .map((i) => [i.statementId!, i]),
  )
  // The supplier's articles, from the slice (`/scope`'s pool, else its candidates):
  // what the Rückfrage draft, the request dialog's fallback text, AskBox's hint and
  // the ARTIKEL slot's empty-pool note read.
  const poolNumbers = slice
    ? slice.poolNumbers.length > 0
      ? slice.poolNumbers
      : slice.candidateNumbers
    : []
  const supplierHasArticles = poolNumbers.length > 0
  for (const list of cards.values())
    for (const card of list) {
      const issue = stepThreeIssues.get(card.id)
      card.openDetail =
        issue && ctx ? openPointDetail(issue, ctx, card.conditions) : null
      if (input.draftedStatements.has(card.id)) card.draftQuestions = true
      card.supplierHasArticles = supplierHasArticles
      card.supplierMatched = slice?.supplierMatched
    }
  const summaryOf = new Map((summary ?? []).map((f) => [f.framework, f]))
  const summaryVerdicts = new Map<string, string | null>()
  for (const f of summary ?? [])
    for (const s of f.statements) summaryVerdicts.set(s.statement_id, s.verdict)
  const units = new Map<string, UnitFacts[]>()
  const derivations = new Map<string, FrameworkDerivation | null>()
  const cardRefsByFramework = new Map<string, CardRef[]>()
  for (const fw of frameworks) {
    const open = cards.get(fw)
    if (fw === activeFramework && open) {
      const live = open.filter((c) => !c.excluded).map(toUnitFacts)
      units.set(fw, live)
      derivations.set(
        fw,
        deriveFrameworkVerdict(live, input.cascadeInquiryStateOf(fw)),
      )
      cardRefsByFramework.set(
        fw,
        open.map((c) => ({
          id: c.id,
          groupIndex: c.groupIndex,
          excluded: c.excluded,
        })),
      )
      continue
    }
    const f = summaryOf.get(fw)
    units.set(fw, f ? unitsFromSummary(f) : [])
    derivations.set(
      fw,
      f ? deriveFromSummary(f, input.cascadeInquiryStateOf(fw)) : null,
    )
    cardRefsByFramework.set(fw, f ? cardRefsFromSummary(f) : [])
  }
  // The open regulation counts with the browser's own derivation only while its slice
  // is on screen. While it loads (a switch to a regulation not yet cached), the
  // server's answer stands for it too: an empty browser list there would read as
  // "nothing open" and let Finish through. The same while the slice on screen may be
  // behind the server (another version than the summary's, or its refetch failed):
  // its own count would show green from a state the server has moved past.
  const browserFramework = ctx && !input.maskUntrusted ? activeFramework : null
  const worklist =
    status === null
      ? flow.worklist
      : mergeWorklist({
          server:
            (status === "unknown" && !input.serverIssuesStand) || !openItemsData
              ? []
              : openItemsToWorklist(openItemsData),
          activeFramework: browserFramework,
          browser: ctx ? flow.worklist : [],
        })
  const regulationStatusOf = (
    framework: string,
  ): "pending" | "unknown" | null =>
    openItemsUnsettled(status) && framework !== browserFramework ? status : null
  const geprueft = (framework: string) =>
    regulationStatusOf(framework) !== null
      ? false
      : frameworkGeprueft(framework, worklist)
  const openByFramework = openWorkByFramework(worklist)
  // The mask waits for the open regulation's slice, and for the summary when no
  // regulation is known yet (a first load without `sfw`): never "no regulation"
  // before the server has said so. A failed slice shows its error instead.
  const maskLoading =
    !input.maskError &&
    (flow.isLoading || (!ctx && (activeFramework !== null || !summary)))
  return {
    declId: input.declId,
    ctx,
    readModel: flow.readModel,
    worklist,
    frameworks,
    regulationNameByFramework,
    activeFramework,
    unitsByFramework: units,
    statementCardsByFramework: cards,
    editionInfo: editionInfoByFramework(flow.readModel, cards),
    derivations,
    geprueft,
    openByFramework,
    openItemsStatus: status,
    regulationStatusOf,
    primaryAction: derivePrimaryAction([...derivations.values()]),
    pool: input.pool,
    poolNumbers,
    issuerProposal: input.issuerProposal,
    uncoveredClaimsByArticle: input.uncoveredClaimsByArticle,
    readerHintsByFramework: input.readerHintsByFramework,
    supplierMatched: input.supplierMatched,
    supplierId: input.supplierId,
    supplierMentioned: input.supplierMentioned,
    memorySupplier: input.memorySupplier,
    supplierSuggestions: input.supplierSuggestions,
    inquiryStateOf: input.inquiryStateOf,
    inquiryArticleCountOf: input.inquiryArticleCountOf,
    inquiryIsDeadAddressOf: input.inquiryIsDeadAddressOf,
    draftedQuestionCountOf: input.draftedQuestionCountOf,
    url: input.url,
    isLoading: input.isLoading,
    isError: input.isError,
    paged: true,
    navigatorLoading: input.navigatorLoading,
    maskLoading,
    navigatorError: input.navigatorError,
    maskError: input.maskError,
    maskRefreshError: input.maskRefreshFailed,
    retryNavigator: input.retryNavigator,
    retryMask: input.retryMask,
    pagedTables: true,
    cardRefsByFramework,
    pendingIssues: flow.session.pendingIssues,
    draftedStatements: input.draftedStatements,
    summaryVerdictOf: (statementId) =>
      (summaryVerdicts.get(statementId) as Verdict | null | undefined) ??
      undefined,
    poolLoading: input.poolLoading,
    poolError: input.poolError,
    retryPool: input.retryPool,
    summaryWriteSeq: openItemsData?.revision?.write_seq ?? null,
    cascadeInquiryStateOf: input.cascadeInquiryStateOf,
  }
}

export function useStudioV2State(declId: string): StudioV2State {
  const { translate } = useTranslation()
  // STEP C3-b2: PAGED OR NOT IS DECIDED ONCE PER PAGE MOUNT. Flags render from the
  // session cache and then refresh, so `useReviewPaged` can change seconds after the
  // page mounted; switching data sources under an open editor would drop its state.
  // A flag change takes effect on the next page load.
  const livePaged = useReviewPaged()
  const [paged] = useState(livePaged)
  // Set once a paged mount fell back to today's full reads before its first paint
  // (`usePagedSource`); from then on the page reads exactly as flag off does.
  const [fellBack, setFellBack] = useState(false)
  const url = useStudioV2Url()
  // C1-d: in paged mode the other regulations' open items come from the server's
  // own list (`serverWorklist.ts`); flag off (and a paged mount that fell back)
  // never sends the read at all.
  const openItemsQ = useDeclarationOpenItems(declId, {
    enabled: paged && !fellBack,
  })
  const openItemsData = openItemsQ.data
  const openItemsIsError = openItemsQ.isError
  const openItemsIsFetching = openItemsQ.isFetching
  // The paged source: the open regulation's slice, chosen from the URL and the
  // server's summary. Called only on a paged mount — `paged` never flips under it —
  // so a flag-off page calls exactly the hooks it always did.
  const pagedSource = paged
    ? // eslint-disable-next-line react-hooks/rules-of-hooks -- fixed per page mount
      usePagedSource(declId, url, openItemsQ, translate, fellBack, setFellBack)
    : null
  const sliceMode = pagedSource?.sliceMode ?? false
  const flow = useDeclarationFlowState(
    declId,
    pagedSource?.sliceMode
      ? {
          slice: pagedSource.slice,
          isLoading: pagedSource.sliceLoading,
          isError: pagedSource.sliceError,
          collisions: pagedSource.collisions,
        }
      : undefined,
  )
  // Paged: no full `/statements` (the slice carries the open regulation's group, the
  // summary the rest); `/scope` (the pool with ids and names, for the article picker,
  // Anfordern and the approved snapshot) loads in the background once the open
  // regulation's slice has landed, never ahead of it.
  const statementsQ = useDeclarationStatements(declId, { enabled: !sliceMode })
  // A declaration with no regulation at all has no slice to wait for: its pool is
  // what the "add a statement" dialog's article list offers (TUR-1942).
  const noFramework = !!pagedSource?.summary && !pagedSource.activeFramework
  const scopeQ = useDeclarationScope(declId, {
    enabled: !sliceMode || (pagedSource?.sliceSettled ?? false) || noFramework,
    paged: sliceMode,
  })
  const issuerProposalOn = useFeatureFlag("MC_ISSUER_SCOPE_PROPOSAL")
  // NOT gated on MC_DECLARATION_INQUIRY, deliberately. The flag governs whether a
  // reviewer may RAISE a Rückfrage; it must never govern whether we can SEE one
  // that is already out. Gating the read here (an earlier attempt to spare a 404
  // before the backend shipped) meant that turning the flag off mid-rollout hid a
  // sent Rückfrage: the navigator dropped its marker and the footer offered
  // Übernehmen, and the reviewer only learned of the block from the server's 409.
  // The backend leaves this GET ungated for exactly that reason. Absent data still
  // reads as "none" downstream — that is a display fallback, and the approve gate
  // stays server-authoritative either way.
  const inquiriesQ = useDeclarationInquiries(declId)
  // The same read AskBox, the footer and the layout already hold (react-query
  // dedupes it): which statements and frameworks carry unsent questions.
  const questionsQ = useDeclarationQuestions(declId, false)
  // MC_DECL_V2_SUP. Same "read it, but never gate the workspace on it"
  // treatment as inquiriesQ just above — see this hook's isLoading/isError
  // comment below for why, and StudioV2State.supplierMatched's own doc for
  // why `null` (not `false`) is the honest default while this is out.
  const extractionQ = useDeclarationExtraction(declId)

  // The `?? []` fallback stays INSIDE the memo body (not up here): a fresh
  // array on every render with no data would otherwise be a fresh dependency
  // every render, defeating the memo entirely. `statementsQ.data` itself is
  // react-query's own stable reference, so depending on it directly is safe.
  const statementsData = statementsQ.data
  const scopeData = scopeQ.data
  // Paged: `/scope` loads after the open slice; until it lands (or fails) the pool is
  // not known yet — not empty.
  const scopePending = scopeData === undefined && !scopeQ.isError
  const scopeFailed = scopeData === undefined && scopeQ.isError
  const refetchScope = scopeQ.refetch
  const inquiriesData = inquiriesQ.data
  const questionsData = questionsQ.data
  const extractionData = extractionQ.data
  // Inquiries are NOT in isLoading/isError: a slow or failed Rückfrage read
  // must never hold the whole workspace on a blank screen — deriveFrameworkVerdict
  // simply reads "none" until the row arrives, the honest default for "no
  // Rückfrage known yet" (identical to a framework that genuinely has none).
  // Extraction joins that same exclusion for the same reason: a slow supplier
  // read must never blank the workspace either — the chip/gate simply stay
  // quiet until it resolves. Paged: the scope read is a background read too.
  // Paged: the page as a whole loads only until it has first committed to its
  // slices (the summary or the open slice landed). After that a regulation switch
  // only puts the mask on its skeleton — the navigator, the footer and the keyboard
  // stay — and a failed read is that pane's error with a retry, never the page-wide
  // error screen (nor is a background `/scope` failure).
  const isLoading = sliceMode
    ? !pagedSource?.committed
    : flow.isLoading || statementsQ.isLoading || scopeQ.isLoading
  const isError = sliceMode
    ? false
    : flow.isError || statementsQ.isError || scopeQ.isError
  const navigatorError = sliceMode && (pagedSource?.summaryError ?? false)
  const maskError = sliceMode && (pagedSource?.sliceError ?? false)
  const maskUntrusted = sliceMode && (pagedSource?.sliceUntrusted ?? false)
  const maskRefreshFailed =
    sliceMode && (pagedSource?.sliceRefreshFailed ?? false)
  const refetchOpenItems = openItemsQ.refetch
  const retryNavigator = useCallback(() => {
    void refetchOpenItems()
  }, [refetchOpenItems])
  const retrySlice = pagedSource?.retrySlice
  const pagedSlice = pagedSource?.slice
  const pagedSummary = pagedSource?.summary
  const pagedActive = pagedSource?.activeFramework ?? null
  const pagedOrder = pagedSource?.order
  const summaryMissing = sliceMode && (pagedSource?.summaryMissing ?? false)

  return useMemo(() => {
    const statementGroups = statementsData ?? []
    const pool = scopeData?.articlePool?.length
      ? scopeData.articlePool
      : (scopeData?.candidateArticles ?? [])
    const poolNumbers = pool.map((a) => a.articleNumber)
    const uncoveredClaimsByArticle = new Map<string, UncoveredClaim[]>()
    for (const claim of flow.readModel?.uncovered_claims ?? []) {
      const list = uncoveredClaimsByArticle.get(claim.article)
      if (list) list.push(claim)
      else uncoveredClaimsByArticle.set(claim.article, [claim])
    }
    const readerHintsByFramework = new Map<string, Map<string, ReaderHint[]>>()
    for (const hint of flow.readModel?.reader_hints ?? []) {
      let byArticle = readerHintsByFramework.get(hint.framework)
      if (!byArticle) {
        byArticle = new Map()
        readerHintsByFramework.set(hint.framework, byArticle)
      }
      const list = byArticle.get(hint.article)
      if (list) list.push(hint)
      else byArticle.set(hint.article, [hint])
    }
    const inquiryRows = inquiriesData ?? []
    const draftedStatements = new Set<string>()
    const draftedQuestionCounts = new Map<string, number>()
    for (const q of questionsData ?? []) {
      if (q.statementId) draftedStatements.add(q.statementId)
      if (q.framework)
        draftedQuestionCounts.set(
          q.framework,
          (draftedQuestionCounts.get(q.framework) ?? 0) + 1,
        )
    }
    const draftedQuestionCountOf = (framework: string) =>
      draftedQuestionCounts.get(framework) ?? 0
    // The wire state, what the navigator glyph and the footer gate read.
    const inquiryStateOf = (framework: string) =>
      inquiryStateFor(inquiryRows, framework)
    const inquiryArticleCountOf = (framework: string) =>
      inquiryArticleCountFor(inquiryRows, framework)
    const inquiryIsDeadAddressOf = (framework: string) =>
      inquiryIsDeadAddressFor(inquiryRows, framework)
    // What the R1-R4 cascade reads: a sent or answered Rückfrage outranks a
    // draft, only "none" becomes "drafted", which deriveFrameworkVerdict
    // turns into R1 with `drafted: true` (Anfordern primary, Freigeben not).
    const cascadeInquiryStateOf = (framework: string): InquiryState => {
      const state = inquiryStateOf(framework)
      return state === "none" && draftedQuestionCountOf(framework) > 0
        ? "drafted"
        : state
    }
    // `null` while extractionData is absent — covers "still loading" AND "read
    // failed/not-yet-shipped" alike, same as inquiryStateOf's "none" default.
    const supplierMatched = extractionData
      ? extractionData.supplierMatched
      : null
    const supplierId = extractionData?.supplier ?? null
    const supplierMentioned = extractionData?.supplierMentioned ?? null
    const memorySupplier = extractionData?.memorySupplier ?? null
    const supplierSuggestions =
      extractionData?.supplierSuggestions ?? NO_SUGGESTIONS
    // C1-d. A backend older than C3-s (no `frameworks`) keeps today's browser
    // derivation for the whole mount; otherwise, in paged mode, the server's
    // answer is "fresh", "pending" or "unknown" (`openItemsStatus`).
    // Once the paged page is on screen, an answer without a summary is NOT "an older
    // backend, use the browser": the browser only knows the open regulation here, so
    // that would let Finish through on it alone. It reads unknown instead.
    const status: OpenItemsStatus | null = summaryMissing
      ? "unknown"
      : paged &&
          !fellBack &&
          !(openItemsData && isOldOpenItemsBackend(openItemsData))
        ? openItemsStatus({
            hasData: openItemsData !== undefined,
            isError: openItemsIsError,
            isFetching: openItemsIsFetching,
          })
        : null
    if (sliceMode)
      return pagedStudioState({
        declId,
        flow,
        translate,
        url,
        isLoading,
        isError,
        navigatorLoading: !pagedSummary && !navigatorError,
        navigatorError,
        maskError,
        maskUntrusted,
        maskRefreshFailed,
        retryNavigator,
        retryMask: retrySlice ?? (() => {}),
        status,
        openItemsData,
        serverIssuesStand: summaryMissing,
        slice: pagedSlice,
        summary: pagedSummary ?? null,
        order: pagedOrder ?? [],
        activeFramework: pagedActive,
        pool,
        poolLoading: scopePending,
        poolError: scopeFailed,
        retryPool: () => void refetchScope(),
        draftedStatements,
        cascadeInquiryStateOf,
        supplierMatched,
        supplierId,
        supplierMentioned,
        memorySupplier,
        supplierSuggestions,
        issuerProposal: issuerProposalOn
          ? (scopeData?.issuerScopeProposal ?? null)
          : null,
        uncoveredClaimsByArticle,
        readerHintsByFramework,
        inquiryStateOf,
        inquiryArticleCountOf,
        inquiryIsDeadAddressOf,
        draftedQuestionCountOf,
      })
    const ctx = flow.ctx
    if (!ctx) {
      return {
        declId,
        ctx: null,
        readModel: flow.readModel,
        worklist: [],
        frameworks: [],
        regulationNameByFramework: new Map(),
        activeFramework: null,
        unitsByFramework: new Map(),
        statementCardsByFramework: new Map(),
        editionInfo: new Map<string, FrameworkEditionInfo>(),
        derivations: new Map(),
        geprueft: () => false,
        openByFramework: new Map(),
        openItemsStatus: status,
        regulationStatusOf: () => null,
        primaryAction: "request" as PrimaryAction,
        pool,
        poolNumbers,
        issuerProposal: null,
        uncoveredClaimsByArticle,
        readerHintsByFramework,
        supplierMatched,
        supplierId,
        supplierMentioned,
        memorySupplier,
        supplierSuggestions,
        inquiryStateOf,
        inquiryArticleCountOf,
        inquiryIsDeadAddressOf,
        draftedQuestionCountOf,
        url,
        isLoading,
        isError,
        ...(paged && { pagedTables: true }),
      }
    }
    const regulationNameByFramework = new Map<string, string | null>(
      statementGroups.map((g) => [g.frameworkSlug, g.regulationName ?? null]),
    )
    const cards = statementCardsByFramework(
      ctx,
      statementGroups,
      flow.readModel,
    )
    const frameworks = documentOrder(orderedFrameworks(ctx), cards, (fw) =>
      frameworkDisplayName(fw, regulationNameByFramework, translate),
    )
    // A link that names a statement but no Verordnung (`declarationReviewPath`, the
    // agent's hand-overs, old Studio V1 links) lands on the Verordnung that holds
    // that statement, so its card can be addressed; otherwise the first one.
    const statementFramework = url.state.statementId
      ? [...cards.entries()].find(([, list]) =>
          list.some((card) => card.id === url.state.statementId),
        )?.[0]
      : undefined
    const activeFramework =
      (url.state.framework && frameworks.includes(url.state.framework)
        ? url.state.framework
        : statementFramework && frameworks.includes(statementFramework)
          ? statementFramework
          : frameworks[0]) ?? null
    const stepThreeIssues = new Map(
      flow.worklist
        .filter((i) => i.step === 3 && i.statementId)
        .map((i) => [i.statementId!, i]),
    )
    for (const list of cards.values()) {
      for (const card of list) {
        const issue = stepThreeIssues.get(card.id)
        card.openDetail = issue
          ? openPointDetail(issue, ctx, card.conditions)
          : null
      }
    }
    if (draftedStatements.size > 0) {
      for (const list of cards.values()) {
        for (const card of list) {
          if (draftedStatements.has(card.id)) card.draftQuestions = true
        }
      }
    }
    // TUR-1652: the live pool is the one "does this supplier have any
    // articles" fact the review holds (the ARTIKEL slot's empty-pool note
    // reads it too), stamped on every card so the Rückfrage draft, the
    // request dialog's fallback body and AskBox's hint all read one source.
    const supplierHasArticles = pool.length > 0
    const scopeSupplierMatched = scopeData?.supplierMatched
    for (const list of cards.values()) {
      for (const card of list) {
        card.supplierHasArticles = supplierHasArticles
        card.supplierMatched = scopeSupplierMatched
      }
    }
    // BUG B (never-stuck): a `card.excluded` (rejected) statement must never
    // contribute to the R1–R4 cascade — it counts for nothing any more (the
    // same "zählt nicht mehr" the collapsed card itself already says). Before
    // this filter, an excluded statement still fed `deriveFrameworkVerdict`
    // as a live unit, so a framework whose ONLY statement got rejected kept
    // deriving off that dead statement's own verdict (e.g. a lone
    // non_compliant exclusion still read R2 head-on) instead of the honest
    // "nothing left to derive" — `deriveFrameworkVerdict` already documents
    // `units.length === 0 → null` for exactly this case. `unitsByFramework`
    // stays this SAME filtered set (not a second, diverging one) — nothing
    // else in this hook reads it besides the derivation loop below.
    const units = new Map<string, UnitFacts[]>(
      [...cards.entries()].map(([fw, list]) => [
        fw,
        list.filter((c) => !c.excluded).map(toUnitFacts),
      ]),
    )
    const derivations = new Map<string, FrameworkDerivation | null>()
    for (const fw of frameworks) {
      derivations.set(
        fw,
        deriveFrameworkVerdict(units.get(fw) ?? [], cascadeInquiryStateOf(fw)),
      )
    }
    // C1-d: the worklist the footer, the open-points map, Finish, Weiter and the
    // navigator read. Flag off: the browser's own. Paged: the server's issues for
    // every regulation but the open one, the browser's for the open one.
    // "unknown" uses no server issue at all (a failed read says nothing), so those
    // regulations read unknown below.
    const worklist =
      status === null
        ? flow.worklist
        : mergeWorklist({
            server:
              status === "unknown" || !openItemsData
                ? []
                : openItemsToWorklist(openItemsData),
            activeFramework,
            browser: flow.worklist,
          })
    // A regulation whose ✓ must not be trusted: every one but the open one while
    // the server's answer is missing ("pending") or failed ("unknown"). A refetch
    // after a gesture ("refreshing") keeps the last answer on screen, so the ✓ do
    // not flicker on every save; Finish waits for it in the footer.
    const regulationStatusOf = (
      framework: string,
    ): "pending" | "unknown" | null =>
      openItemsUnsettled(status) && framework !== activeFramework
        ? status
        : null
    // Never green from a missing answer: with no server answer at all (none yet,
    // or the read failed) the other regulations are not geprueft.
    const geprueft = (framework: string) =>
      regulationStatusOf(framework) !== null
        ? false
        : frameworkGeprueft(framework, worklist)
    const openByFramework = openWorkByFramework(worklist)
    return {
      declId,
      ctx,
      readModel: flow.readModel,
      worklist,
      frameworks,
      regulationNameByFramework,
      activeFramework,
      unitsByFramework: units,
      statementCardsByFramework: cards,
      // Referenzliste per Verordnung (catalogue ∪ card values, selected name,
      // document-cited unknown editions) — the unit card's edition row reads
      // this; see mapping/editionInfo.ts for why V2 must carry it at all.
      editionInfo: editionInfoByFramework(flow.readModel, cards),
      derivations,
      geprueft,
      openByFramework,
      openItemsStatus: status,
      regulationStatusOf,
      primaryAction: derivePrimaryAction([...derivations.values()]),
      pool,
      poolNumbers,
      issuerProposal: issuerProposalOn
        ? (scopeData?.issuerScopeProposal ?? null)
        : null,
      uncoveredClaimsByArticle,
      readerHintsByFramework,
      supplierMatched,
      supplierId,
      supplierMentioned,
      memorySupplier,
      supplierSuggestions,
      inquiryStateOf,
      inquiryArticleCountOf,
      inquiryIsDeadAddressOf,
      draftedQuestionCountOf,
      url,
      isLoading,
      isError,
      ...(paged && { pagedTables: true }),
    }
  }, [
    declId,
    flow,
    sliceMode,
    pagedSlice,
    pagedSummary,
    pagedActive,
    pagedOrder,
    paged,
    openItemsData,
    openItemsIsError,
    openItemsIsFetching,
    summaryMissing,
    statementsData,
    scopeData,
    issuerProposalOn,
    scopeFailed,
    refetchScope,
    scopePending,
    inquiriesData,
    questionsData,
    extractionData,
    url,
    isLoading,
    isError,
    navigatorError,
    maskError,
    maskUntrusted,
    maskRefreshFailed,
    retryNavigator,
    retrySlice,
    fellBack,
    translate,
  ])
}
